/**
 * home/data/homeFeatures.ts
 * Datos de las 5 features expandibles (acordeón)
 * 
 * AUDITORÍA 2026-03-02 (contra STATUS.md + cascada real):
 * - Coordenadas: 62 formatos (sin cambio)
 * - UTF-8: 116 patrones (sin cambio)
 * - Validación: Scoring 3D (D1+D2+D3) + supramunicipal + cross-validation
 * - Geocodificación: 14 niveles R2 + fallback online (era 11)
 * - Exportación: 10 formatos (era 8) — +PDF informe +HTML trimestral
 */
import {
  IconMapPin, IconLanguage, IconShieldCheck, IconSearch, IconDownload,
} from '../icons';
import type { FeatureData } from './types';

export const FEATURES: FeatureData[] = [
  {
    id: 'coords',
    icon: IconMapPin,
    title: 'Normalización de coordenadas',
    badge: '62 formatos',
    desc: 'Detecta y convierte cualquier formato de coordenadas a UTM30 ETRS89',
    beforeLabel: 'Entrada',
    beforeContent: ["37°53'24\"N, 4°46'12\"O", '37.4612, -3.9231', '30S 395842 4148523', '456.789,12 (formato EU)'],
    afterLabel: 'Salida UTM30',
    afterContent: ['X: 343521.67', 'Y: 4197845.23', 'CRS: EPSG:25830', 'Huso: 30N'],
    listTitle: 'Formatos soportados',
    listTags: [
      'Grados decimales', 'Sexagesimal (° \' ")', 'UTM con zona', 'UTM sin zona',
      'Formato europeo (coma)', 'ED50 → ETRS89', 'WGS84 → UTM30', 'NMEA',
    ],
  },
  {
    id: 'utf8',
    icon: IconLanguage,
    title: 'Corrección UTF-8',
    badge: '116 patrones',
    desc: 'Recupera tildes, eñes y caracteres dañados por conversiones entre sistemas',
    beforeLabel: 'Texto corrupto',
    beforeContent: ['ComunicaciÃ³n', 'PeÃ±a del Ãguila', 'NÂº 3', 'Ctra. MoÃ±as'],
    afterLabel: 'Texto limpio',
    afterContent: ['Comunicación', 'Peña del Águila', 'Nº 3', 'Ctra. Moñas'],
    listTitle: 'Motor de 3 niveles de prioridad',
    listTags: [
      'Patrones críticos, siempre activos (37)', 'Patrones frecuentes, 2.ª pasada (41)', 'Patrones raros, último recurso (33)',
      'Normalizador de caracteres (5 reglas)', 'Detección de codificación doble', 'Salida rápida si texto limpio',
    ],
  },
  {
    id: 'validation',
    icon: IconShieldCheck,
    title: 'Validación multicapa',
    badge: 'Scoring 3D · 0-100',
    desc: 'Tres dimensiones de calidad (Origen + Precisión + Validación) con validación supramunicipal y cross-validation multifuente',
    beforeLabel: 'Sin validar',
    beforeContent: ['Score: ?', 'D1/D2/D3: desconocido', 'Municipio: sin verificar', 'Fuentes cruzadas: 0'],
    afterLabel: 'Validado',
    afterContent: ['Score: 94/100 ✓', 'D1: 30 · D2: 34 · D3: 30', 'INE: 14055 (Priego) ✓', 'Cross-val: 3 fuentes ✓'],
    listTitle: 'Dimensiones y estrategias',
    listTags: [
      'D1 Origen (fuente + método)', 'D2 Precisión (dígitos + CRS)', 'D3 Validación (espacial + INE)',
      'Perímetro municipal DERA', 'Supramunicipal (radios por tipología)', 'Validación cruzada multifuente',
      'Detección de centroides', 'Detección de duplicados',
    ],
  },
  {
    id: 'geocoding',
    icon: IconSearch,
    title: 'Geocodificación automática',
    badge: '14 niveles R2 · 18 fuentes',
    desc: 'Cascada de 14 fuentes precargadas en Cloudflare R2 (respuesta instantánea) más 4 APIs en línea como fallback (18 fuentes en total)',
    beforeLabel: 'Sin coordenadas',
    beforeContent: ['Hospital San Juan de Dios', 'Córdoba', 'X: —', 'Y: —'],
    afterLabel: 'Geocodificado',
    afterContent: ['Hospital San Juan de Dios', 'INE: 14021', 'X: 343892.45', 'Y: 4197234.78'],
    listTitle: 'Cascada nivel 1 (14 fuentes en R2) + respaldo en línea',
    listTags: [
      'CDAU R2 (3,2M portales)', 'NGA R2 (1,8M topónimos)', 'DERA genérico (11K)',
      'DERA G10 energía (8,3K)', 'DERA G3 hidráulica (26K)', 'IAPH patrimonio (18,9K)',
      'DERA Viaria (184K tramos)', 'AGOL complementarios (36K)', 'Catastro R2 (2,4M)',
      'CGPHA catalogados (18,5K)', 'OBPH no catalogados (8,3K)', 'OSM Seguridad (791)',
      'Edu Centers (7,1K)', 'GC Facilities (629)',
      'CartoCiudad (IGN)', 'CDAU en línea', 'IDEE', 'Nominatim',
    ],
  },
  {
    id: 'export',
    icon: IconDownload,
    title: 'Exportación SIG',
    badge: '10 formatos · EPSG:25830',
    desc: 'Datos listos para QGIS, ArcGIS y cualquier SIG, más informes PDF formales e informes HTML trimestrales',
    beforeLabel: 'Documento PTEL',
    beforeContent: ['PTEL_Priego.odt', '45 infraestructuras', 'Formato: tabla ODT', 'CRS: mixto/desconocido'],
    afterLabel: 'Capa GIS + Informes',
    afterContent: ['Priego_infras.geojson', '45 features validadas', 'CRS: EPSG:25830', 'PDF informe + HTML trimestral'],
    listTitle: 'Formatos de exportación',
    listTags: [
      'GeoJSON', 'CSV con coordenadas', 'XLSX (11 pestañas)', 'WKT',
      'GeoPackage', 'Shapefile', 'KML/KMZ', 'PDF informe formal',
      'HTML informe trimestral', 'Metadatos INSPIRE',
    ],
  },
];
